/**
 * useMenu hook.
 */
import Focus from '@shared/types/enums/focus';
import Pages from '@pages';
import { focusSelector, setFocus } from '@shared/store/slices/focus.slice';
import { modalSelector } from '@shared/store/slices/modal.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { DURATION, TRANSITION } from '@shared/types/constants/animations';
import { MENU_PAGES_BLACKLIST } from '@shared/types/constants/pageVisibility';
import {
  setMenuCollapse,
  setMenuDisplay,
} from '@shared/store/slices/menu.slice';
import {
  selectCurrentParams,
  selectRoute,
} from '@shared/store/slices/router.slice';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import {
  MENU_FOCUS_BLACKLIST,
  MENU_FOCUS_WHITELIST,
} from '@shared/types/constants/focusVisibility';

/**
 * Hook to manage menu display and animation.
 * @returns {Object} Menu display, animation, data, and index
 */
export default function useMenu() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const { focus, stack } = useAppSelector(focusSelector);
  const { current: modal } = useAppSelector(modalSelector);
  const { Page } = useAppSelector(selectRoute);
  const currentParams = useAppSelector(selectCurrentParams);

  // Refs
  const effectiveIndex = useRef(0);
  const frozenCollapseRef = useRef(true);
  const setListIndex = useRef<(newIndex: number, increment?: number) => void>(
    () => null
  );

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  /**
   * Collapses or expands the menu based on the current focus.
   */
  const collapse = useMemo(() => {
    if (modal || MENU_FOCUS_BLACKLIST.includes(focus as number)) {
      return frozenCollapseRef.current;
    }
    frozenCollapseRef.current =
      !MENU_FOCUS_WHITELIST.includes(focus as number) &&
      !stack.includes(Focus.MENU) &&
      !currentParams?.deadman;
    return frozenCollapseRef.current;
  }, [currentParams, focus, modal, stack]);

  /**
   * Whether the menu should be displayed for the current page.
   */
  const display = useMemo(
    () => !MENU_PAGES_BLACKLIST.includes(Page) || focus === Focus.SPLASH,
    [focus, Page]
  );

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  const onMouseEnter = useCallback(() => {
    if (!display) return;
    if (focus === Focus.FLOATING_MENU) return;
    if (Page === Pages.SEARCH) dispatch(setFocus(Focus.SEARCH));
    else dispatch(setFocus(Focus.MENU));
  }, [display, focus, Page]);

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  /**
   * Broadcast the collapse state. Layout effect so `menu.slice.collapse`
   * (and anything deriving `menuExpanded` from it, e.g. the rails
   * Animator) settles before the first paint rather than a frame later —
   * otherwise a page remounting on back-navigation can paint once with a
   * transient `collapse` value and visibly animate to the corrected one.
   */
  useLayoutEffect(() => {
    dispatch(setMenuCollapse(collapse));
  }, [collapse]);

  useEffect(() => {
    dispatch(setMenuDisplay(display));
  }, [display]);

  // ------------------------------------------------------------
  // Memoized return value
  // ------------------------------------------------------------

  return useMemo(() => {
    return {
      display,
      context: {
        effectiveIndex,
        setListIndex,
      },
      props: {
        container: {
          collapse,
          duration: DURATION.MENU_CONTAINER,
          display,
          transition: TRANSITION.MENU_CONTAINER(collapse),
          onMouseEnter,
        },
        mask: {
          display: !collapse && display,
        },
      },
    };
  }, [collapse, display, setListIndex, onMouseEnter]);
}
